import { ref, reactive, computed } from 'vue'
import { apiRequest } from '../services/apiClient'
import { getValidToken, hasRefreshableSession } from '../services/authService'

/**
 * Глобальний стан переписки з водіями та персоналом.
 *
 * Живе поза сторінками: вікно чату відкрите доти, доки користувач сам його не
 * закриє, і не зникає при переході між розділами. Тому і стан, і поллінг —
 * на рівні модуля, а не компонента.
 */

const UNREAD_POLL_MS = 15000 // лічильники непрочитаних у меню та картках
const CHAT_POLL_MS = 5000    // активна переписка

function withAuth(options = {}) {
  const token = getValidToken()
  if (!token) {
    // Токен в памяти протух (вкладка висела в фоне дольше TTL access-токена).
    // Это не разлогин: refresh-cookie жива, apiClient сам сделает silentRefresh
    // и подставит свежий токен. Падаем только когда сессии нет вообще.
    if (hasRefreshableSession()) return options
    throw new Error('token_required')
  }
  return {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  }
}

// --- Стан ---
const unreadTotal = ref(0)
const unreadByDriver = ref({})
// Розбивка за kind: водії та персонал лежать в одній таблиці, але в меню це
// різні пункти — бейдж має світитися на своєму.
const unreadByKind = ref({ driver: 0, personnel: 0 })
const chat = reactive({
  open: false,
  minimized: false,
  driverId: null,
  driverName: '',
  kind: 'driver',
  telegramLinked: true,
  messages: [],
  loading: false,
  sending: false,
  error: '',      // помилка надсилання
  loadError: '',  // помилка завантаження історії
})

let unreadTimer = null
let chatTimer = null
let lastMessageId = 0
// Звук лише для повідомлень, що прийшли після відкриття вкладки: інакше при
// першому завантаженні історії програвався б сигнал на всю стару переписку.
let soundArmed = false

// --- Звук нового повідомлення ---
// Генеруємо через WebAudio, щоб не тягнути в бандл аудіофайл.
let audioCtx = null
function playIncomingSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return
    if (!audioCtx) audioCtx = new Ctx()
    // resume() реджектится на iOS без жеста пользователя — синхронный try/catch не ловит промис.
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {})

    const now = audioCtx.currentTime
    const gain = audioCtx.createGain()
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35)
    gain.connect(audioCtx.destination)

    // Два коротких тони — впізнаваний «дінь», як у месенджерах.
    for (const [freq, at] of [[880, 0], [1180, 0.09]]) {
      const osc = audioCtx.createOscillator()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, now + at)
      osc.connect(gain)
      osc.start(now + at)
      osc.stop(now + at + 0.18)
    }
  } catch (e) {
    // Звук — необов'язкова прикраса, будь-яка помилка не має ламати чат.
  }
}

// --- Лічильники непрочитаних ---
async function refreshUnread() {
  if (!getValidToken()) return
  try {
    const res = await apiRequest('/chat/unread', withAuth())
    const prevTotal = unreadTotal.value
    unreadTotal.value = res?.total || 0
    unreadByDriver.value = res?.byDriver || {}
    unreadByKind.value = {
      driver: res?.byKind?.driver || 0,
      personnel: res?.byKind?.personnel || 0,
    }
    if (soundArmed && unreadTotal.value > prevTotal) {
      playIncomingSound()
    }
    soundArmed = true
  } catch (e) {
    // Мовчки: лічильник не критичний, наступний тик спробує знову.
  }
}

export function startChatPolling() {
  if (unreadTimer) return
  refreshUnread()
  unreadTimer = setInterval(refreshUnread, UNREAD_POLL_MS)
}

export function stopChatPolling() {
  if (unreadTimer) clearInterval(unreadTimer)
  unreadTimer = null
  soundArmed = false
}

// --- Переписка ---
async function loadMessages({ incremental = false } = {}) {
  if (!chat.driverId) return
  const query = incremental && lastMessageId ? `?afterId=${lastMessageId}` : '?limit=100'
  try {
    const res = await apiRequest(`/driver/${chat.driverId}/chat${query}`, withAuth())
    const fresh = res?.messages || []
    if (res?.driver) {
      chat.driverName = res.driver.fullName || chat.driverName
      chat.telegramLinked = res.driver.telegramLinked !== false
      chat.kind = res.driver.kind || 'driver'
    }
    if (!fresh.length) return

    if (incremental) {
      chat.messages.push(...fresh)
      // Дзвіночок лише на вхідні: власні надіслані повідомлення не сигналять.
      if (fresh.some(m => m.direction === 'in')) playIncomingSound()
    } else {
      chat.messages = fresh
    }
    lastMessageId = chat.messages.length ? chat.messages[chat.messages.length - 1].id : 0
    markRead()
    chat.loadError = ''
  } catch (e) {
    chat.loadError = e?.message || 'error'
  }
}

async function markRead() {
  if (!chat.driverId || !lastMessageId) return
  try {
    await apiRequest(`/driver/${chat.driverId}/chat/read`, {
      method: 'POST',
      body: JSON.stringify({ lastMessageId }),
      ...withAuth(),
    })
    // Одразу гасимо лічильник цього водія, не чекаючи наступного тика.
    const map = { ...unreadByDriver.value }
    const was = map[String(chat.driverId)] || 0
    if (was) {
      delete map[String(chat.driverId)]
      unreadByDriver.value = map
      unreadTotal.value = Math.max(0, unreadTotal.value - was)
      // Той самий лічильник у розрізі kind, інакше бейдж у меню довисить до тика.
      const kind = chat.kind === 'personnel' ? 'personnel' : 'driver'
      unreadByKind.value = {
        ...unreadByKind.value,
        [kind]: Math.max(0, (unreadByKind.value[kind] || 0) - was),
      }
    }
  } catch (e) {
    // Не критично: позначка прочитання оновиться при наступному відкритті.
  }
}

export async function openChat(driver) {
  const driverId = driver?.id
  if (!driverId) return

  if (chat.driverId !== driverId) {
    chat.messages = []
    lastMessageId = 0
  }
  chat.driverId = driverId
  chat.driverName = driver.fullName || `${driver.lastName || ''} ${driver.firstName || ''}`.trim()
  chat.telegramLinked = !!driver.telegramChatId
  chat.kind = driver.kind || 'driver'
  chat.open = true
  chat.minimized = false
  chat.error = ''
  chat.loadError = ''
  chat.loading = true

  await loadMessages()
  chat.loading = false

  if (chatTimer) clearInterval(chatTimer)
  chatTimer = setInterval(() => loadMessages({ incremental: true }), CHAT_POLL_MS)
}

export function closeChat() {
  chat.open = false
  chat.driverId = null
  chat.messages = []
  lastMessageId = 0
  if (chatTimer) clearInterval(chatTimer)
  chatTimer = null
}

export async function sendMessage(text) {
  const message = String(text || '').trim()
  if (!message || !chat.driverId || chat.sending) return false
  chat.sending = true
  chat.error = ''
  try {
    await apiRequest(`/driver/${chat.driverId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message }),
      ...withAuth(),
    })
    await loadMessages({ incremental: true })
    chat.error = ''
    return true
  } catch (e) {
    chat.error = e?.message || 'send_failed'
    return false
  } finally {
    chat.sending = false
  }
}

export function useDriverChat() {
  return {
    chat,
    unreadTotal: computed(() => unreadTotal.value),
    unreadByKind: computed(() => unreadByKind.value),
    unreadFor: (driverId) => unreadByDriver.value[String(driverId)] || 0,
    openChat,
    closeChat,
    sendMessage,
    startChatPolling,
    stopChatPolling,
  }
}
